import React from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../Button/PrimaryButton";
import SecondaryButton from "../Button/SecondaryButton";

function ErrorFallback({ error, resetErrorBoundary }) {
  const navigate = useNavigate();
  const isDev = process.env.NODE_ENV !== "production";

  const handleGoHome = () => {
    resetErrorBoundary?.();
    navigate("/", { replace: true });
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      className="min-h-[240px] flex flex-col items-stretch justify-center gap-4 p-6 rounded-xl border border-[var(--border-color-light)] bg-[var(--bg-standard)] shadow-[0_10px_40px_-12px_rgba(37,43,55,0.12)] max-w-lg mx-auto my-8"
      role="alert"
    >
      <div>
        <h2 className="text-lg text-center font-semibold text-gray-800">
          Something went wrong
        </h2>
        <p className="text-sm text-gray-600 mt-2 text-center">
          {isDev
            ? error?.message || "An unexpected error occurred."
            : "We hit an unexpected issue. Try reloading or head back home."}
        </p>
      </div>
      <div className="flex flex-wrap gap-4 justify-center w-full mt-3">
        <PrimaryButton
          onClick={handleReload}
          startContent={false}
          className="hover:opacity-90"
        >
          Reload page
        </PrimaryButton>
        <SecondaryButton title="Go home" onClick={handleGoHome}>
          Go home
        </SecondaryButton>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("Error caught in ErrorBoundary:", error, info);
    if (
      typeof window !== "undefined" &&
      typeof window.reportError === "function"
    ) {
      try {
        window.reportError(error);
      } catch {
        /* ignore */
      }
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-[90dvh] w-full">
          <ErrorFallback
            error={this.state.error}
            resetErrorBoundary={this.resetErrorBoundary}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
